/**
 * Tenant table component
 */

import React from 'react';
import type { Tenant } from '../types';
import { Badge } from './ui/badge';

interface TenantTableProps {
  tenants: Tenant[];
  onView: (tenantId: string) => void;
  isLoading?: boolean;
  startIndex?: number;
}

const formatStatusDisplay = (status: string) => {
  switch (status) {
    case 'ACTIVE':
      return 'Active';
    case 'INACTIVE':
      return 'Inactive';
    case 'TRIAL_REGISTERED':
      return 'Registered';
    case 'TRIAL_UNREGISTERED':
      return 'Trial';
    case 'MIGRATION':
      return 'Migration';
    case 'ARCHIVED':
      return 'Archived';
    case 'PROVISION':
      return 'Provisioning';
    case 'ERROR':
      return 'Error';
    case 'SUBSCRIBED':
      return 'Subscribed';
    default:
      return status;
  }
};

export function TenantTable({ tenants, onView, isLoading, startIndex = 0 }: TenantTableProps) {
  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-12 bg-gray-200 rounded mb-4"></div>
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <table className="data-table tenant-table">
      <thead>
        <tr>
          <th>#</th>
          <th>Tenant ID</th>
          <th>Company Name</th>
          <th>Contact</th>
          <th>Company Size</th>
          <th>Deployment</th>
          <th>Quantity</th>
          <th>Status</th>
          <th>Renewal Date</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {tenants.map((tenant, index) => (
          <tr key={`${tenant.id}-${index}`}>
            <td>{startIndex + index + 1}</td>
            <td>{tenant.id}</td>
            <td>{tenant.company}</td>
            <td>
              <div className="creator-info">
                <div className="creator-name">{tenant.creators?.[0]?.name}</div>
                <div className="creator-email">{tenant.creators?.[0]?.email}</div>
              </div>
            </td>
            <td>{tenant.companySize}</td>
            <td>{tenant.deploymentId}</td>
            <td>{tenant.numberOfLicenses}</td>
            <td>
              <Badge variant={tenant.status}>
                {formatStatusDisplay(tenant.status)}
              </Badge>
            </td>
            <td>{tenant.lastModified}</td>
            <td>
              <div className="row-actions">
                <button
                  className="access-btn access-secondary"
                  onClick={() => onView(tenant.id)}
                  title="View tenant"
                >
                  View
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
